import styled, { css } from "styled-components";
import { IonHeader, IonIcon, IonSpinner } from "@ionic/react";
import Page from "../components/Page";
import UserService from "../services/UserService";
import ToastService from "../services/ToastService";
import { arrayEquals, deepCopy, isEmpty } from "../utils/objects";
import { setUserData } from "../actions/UserActions";
import withNav from "../utils/hoc/withNav";
import { connect } from "react-redux";
import { bindActionCreators } from "@reduxjs/toolkit";
import { chevronBackOutline } from "ionicons/icons";
import { useHistory } from "react-router";
import { useEffect, useState } from "react";
import {
	ModalOverlay,
	Modal,
	ModalButtonGroup,
	ModalHeader,
	ModalPrimaryButton,
	ModalSecondaryButton,
	ModalSubtitle,
} from "../components/Modal";
import Analytics, { TRACK_PAGE_CATEGORIES } from "../lib/anayltics";
import { storeFoodData } from "../actions/FoodActions";
import FoodService from "../services/FoodService";

const PageHeader = styled(IonHeader)`
	position: fixed;
	margin: 0 0 0 0;
	background: ${({ theme }) => theme.colors.main.background};
	font-family: inherit;
	font-style: normal;
	font-size: 20px;
	display: flex;
	align-items: center;
	padding: 1.5rem 1rem 0.5rem 1rem;
	border: none;
	box-shadow: none;
	&:after {
		background-image: none;
	}
`;

const BackButton = styled.div``;

const BackButtonImg = styled(IonIcon)`
	width: 24px;
	height: 24px;
	color: #fff;
	/* Icon alignment */
	margin-left: -5px;
`;

const PageTitle = styled.div`
	/* Keeping it centered becuase of icon alignment */
	font-style: normal;
	font-weight: 500;
	font-size: 20px;
	letter-spacing: 0px;
	margin-left: auto;
	margin-right: auto;
`;

const PaddedElement = styled.div`
	margin: 5rem 0 0 0;
`;

const Preference = styled.div<any>`
	font-size: 14px;
	border-radius: 2px;
	border: 1px solid #e8e8e8;
	padding: 0.5rem 0.3rem;
	min-width: 60px;
	min-height:33px;
	display:flex;
	justify-content:center;
	align-items:center;
	${(props) =>
		props.active &&
		css`
			border: none;
			background: #4b4b4b;
		`}
`;

const Header = styled.div`
	color: #f2f2f2;
	font-size: 18px;
	font-style: normal;
	font-weight: 600;
	line-height: normal;
	width: 100%;
	margin: 0.5rem 0 0 0;
`;

const P1 = styled.div`
	color: #e8e8e8;
	font-size: 14px;
	font-style: normal;
	font-weight: 600;
	line-height: normal;
	margin: 0.5rem 0;
`;

const P2 = styled.div<any>`
	color: #d6d6d6;
	font-size: 12px;
	font-style: normal;
	font-weight: 400;
	line-height: normal;
	text-align: left;
`;

const Hightlighted = styled.span`
	color: #e86262;
`;

const Container = styled.div<any>`
	display: grid;
	gap: 1rem;
	grid-template-columns: repeat(3, 1fr);
	width: 100%;
	height: max-content;
	margin: 2rem 0;
`;

const BottomCta = styled.div<any>`
	position: absolute;
	bottom: 0;
	height: 62px;
	padding: 0 1rem;
	display: flex;
	justify-content: center;
	align-items: center;
	background: #ffcc00;
	color: #000;
	width: 100%;
	z-index: 101;
	font-weight: 600;
	font-size: 18px;
	${(props) =>
		props.disabled &&
		css`
			&:after {
				content: "";
				position: absolute;
				bottom: 0;
				width: 100%;
				height: 100%;
				background: rgba(0, 0, 0, 0.9);
				opacity: 0.5;
				z-index: 100;
			}
			pointer-events: none;
		`}
`;

const Center = styled.div`
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	height: 100%;
	width: 100%;
`;

const Error = styled.div`
	font-weight: 600;
	font-size: 20px;
	color: #fff;
	width: 100%;
`;

const DietaryPreferences = (props: any) => {
	const { allergies, dietaryPreferences, setUserData, initialSetup, storeFoodData, isNewUser, destinationAfterOnboard } = props;
	const history = useHistory();
	const initialLoading = isEmpty(allergies) ? true : false;
	const [isFetching, toggleIsFetching] = useState(initialLoading);
	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState(false);
	const [newDietaryPreferences, setNewDietaryPreferences] = useState(dietaryPreferences);
	const [hasChanges, setHasChanges] = useState(false);
	const [successModal, showSuccessModal] = useState(false);

	useEffect(() => {
		if (dietaryPreferences && newDietaryPreferences) {
			const areEqual = arrayEquals(dietaryPreferences, newDietaryPreferences);
			if (areEqual) setHasChanges(false);
			else setHasChanges(true);
		}
	}, [dietaryPreferences, newDietaryPreferences]);

	async function getAllergies() {
		toggleIsFetching(true);
		const { data, error } = await FoodService.getAllergies();
		if (data) {
			storeFoodData({ allergens: data });
			setIsLoading(false);
		}
		if (error) {
			ToastService.showToast("Could not load Allergens");
			setIsLoading(false);
		}
		toggleIsFetching(false);
	}

	function onPageLoad() {
		if (initialSetup && !isNewUser) {
			history.push("/");
			return;
		}

		Analytics.trackPage(TRACK_PAGE_CATEGORIES.PAGE_LOAD, "Dietary Restrictions");
		if (isEmpty(allergies)) {
			getAllergies();
		}
	}

	useEffect(() => {
		onPageLoad();
	}, []);

	function toggleAllergy(dietaryPreferences: any, allergyId: string) {
		let updatedDietaryPreferences = deepCopy(dietaryPreferences);
		if (checkUserisAllergic(dietaryPreferences, allergyId)) {
			updatedDietaryPreferences = updatedDietaryPreferences.filter((userAllergyId: string) => userAllergyId !== allergyId);
		} else {
			updatedDietaryPreferences.push(allergyId);
		}
		setNewDietaryPreferences(updatedDietaryPreferences);
	}

	function checkUserisAllergic(dietaryPreferences: any, allergyId: string) {
		if (dietaryPreferences && dietaryPreferences.includes(allergyId)) {
			return true;
		}
		return false;
	}

	async function updateUserAllergies() {
		setIsLoading(true);
		const response = await UserService.updateUserDietaryPreferences({ allergens: newDietaryPreferences });
		const { data, error } = response;
		if (data) {
			const { allergens } = data;
			setUserData({ allergens });
			showSuccessModal(true);
		}
		if (error) {
			setError(true);
		}
		setIsLoading(false);
	}

	function renderAllergies(allergyList: any, dietaryPreferences: any) {
		return allergyList && Object.keys(allergyList).length
			? Object.entries(allergyList).map(([key, value]: any) => {
					return (
						<Preference
							active={checkUserisAllergic(dietaryPreferences, key)}
							onClick={() => {
								toggleAllergy(dietaryPreferences, key);
							}}>
							{value}
						</Preference>
					);
			  })
			: "";
	}

	return (
		<Page textAlign="">
			<PageHeader>
				{!initialSetup ? (
					<BackButton>
						<BackButtonImg
							src={chevronBackOutline}
							onClick={() => {
								history.goBack();
							}}
						/>
					</BackButton>
				) : (
					""
				)}
				<PageTitle>Dietary Restrictions</PageTitle>
			</PageHeader>
			<PaddedElement />
			<Header>Ingredients to avoid</Header>
			{!isFetching ? (
				<P1>
					Select the items you can't or don't want to eat. Items that contain significant amounts of these properties will be
					flagged in <Hightlighted>red.*</Hightlighted>
				</P1>
			) : (
				""
			)}
			{isFetching ? (
				<Center>
					<IonSpinner name="dots" color={"#F2F2F2"} />
				</Center>
			) : (
				<Container>{renderAllergies(allergies, newDietaryPreferences)}</Container>
			)}
			{!isFetching ? (
				<P2>
					* While we take steps to minimize risk and provide ingredient information, we cannot guarantee that any of our products are safe to consume for people with severe food allergies.
				</P2>
			) : (
				""
			)}
			<BottomCta onClick={updateUserAllergies} disabled={isLoading || isFetching || (!initialSetup && !hasChanges)}>
				{isLoading ? <IonSpinner name="dots" color={"#F2F2F2"}></IonSpinner> : "SAVE"}
			</BottomCta>

			{successModal && !initialSetup ? (
				<ModalOverlay>
					<Modal>
						<ModalHeader>Hurray!</ModalHeader>
						<ModalSubtitle>We have successfully updated your dietary restrictions.</ModalSubtitle>
						<ModalButtonGroup>
							<ModalPrimaryButton onClick={() => showSuccessModal(false)}>OKAY</ModalPrimaryButton>
						</ModalButtonGroup>
					</Modal>
				</ModalOverlay>
			) : (
				""
			)}

			{successModal && initialSetup ? (
				<ModalOverlay>
					<Modal>
						<ModalHeader>Hurray!</ModalHeader>
						<ModalSubtitle>
							We have successfully updated your dietary restrictions.You can alsways change it by clicking on "Account Icon" under
							"Dietary Preferences"
						</ModalSubtitle>
						<ModalButtonGroup>
							<ModalPrimaryButton
								onClick={() => {
									showSuccessModal(false);
									history.push(destinationAfterOnboard);
								}}>
								OKAY
							</ModalPrimaryButton>
						</ModalButtonGroup>
					</Modal>
				</ModalOverlay>
			) : (
				""
			)}

			{error ? (
				<ModalOverlay>
					<Modal>
						<ModalHeader>Oops!</ModalHeader>
						<ModalSubtitle>
							Due to some technical difficulties we weere not able to update your dietary restrictions, please try again later
						</ModalSubtitle>
						<ModalButtonGroup>
							<ModalSecondaryButton
								onClick={() => {
									setError(false);
								}}>
								CANCEL
							</ModalSecondaryButton>
							<ModalPrimaryButton
								onClick={() => {
									updateUserAllergies();
									setError(false);
								}}>
								TRY AGAIN
							</ModalPrimaryButton>
						</ModalButtonGroup>
					</Modal>
				</ModalOverlay>
			) : (
				""
			)}
		</Page>
	);
};

//Redux mapping
const mapStateToProps = (state: any) => {
	const {
		User: { dietaryPreferences, isNewUser, destinationAfterOnboard },
		Food: { allergies },
	} = state;
	return { dietaryPreferences, allergies, isNewUser, destinationAfterOnboard };
};

const mapDispatchToProps = (dispatch: any) => ({
	...bindActionCreators(
		{
			setUserData,
			storeFoodData,
		},
		dispatch
	),
});

export default connect(mapStateToProps, mapDispatchToProps)(withNav(DietaryPreferences));
